// Engine-specific pseudo-table bindings discovered by inspecting the
// statement's token stream. These are aliases that are syntactically visible
// without an explicit FROM/JOIN entry — emitted by:
//   • MSSQL `OUTPUT` / `MERGE`            → inserted, deleted, $action
//   • PG / SQLite `ON CONFLICT`           → excluded
//   • MySQL `INSERT … AS new`             → new
//   • CREATE TRIGGER (PG/MySQL/SQLite)    → NEW, OLD (+ TG_OP, FOUND for PG)
//   • CREATE TRIGGER (Oracle)             → :NEW, :OLD
//
// The relation field uses a synthetic name (e.g. `'__pseudo:inserted'`) so
// the orchestrator can recognise the binding as a pseudo-table and look up
// columns from the *target* of the surrounding DML rather than from the
// schema cache. Today we only emit the alias structure; the resolver slice
// pairs the alias with the target's columns.

import type {
  AliasBinding,
  CompletionCandidate,
  DatabaseEngineId,
  OffsetRange,
} from '../../../core/engines';
import { tokenize } from '../context/tokenizer';

export function collectPseudoTables(
  source: string,
  statementRange: OffsetRange,
  engineId: DatabaseEngineId,
): readonly AliasBinding[] {
  const tokens = tokenize(source, statementRange.start, statementRange.end, engineId);
  if (tokens.length === 0) return [];
  const out: AliasBinding[] = [];

  let sawOutput = false;
  let sawMerge = false;
  let sawOnConflict = false;
  let sawInsertAsNew = false;
  let sawTrigger = false;

  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok.type === 'kw' || tok.type === 'ident') {
      const upper = tok.text.toUpperCase();
      if (tok.type === 'kw' && upper === 'OUTPUT') sawOutput = true;
      else if (tok.type === 'kw' && upper === 'MERGE') sawMerge = true;
      else if (tok.type === 'kw' && upper === 'ON' && nextWordIs(tokens, i + 1, 'CONFLICT')) {
        sawOnConflict = true;
      } else if (tok.type === 'kw' && upper === 'AS' && nextWordIs(tokens, i + 1, 'NEW') && hasInsertContext(tokens)) {
        sawInsertAsNew = true;
      } else if (upper === 'CREATE') {
        // CREATE TRIGGER  |  CREATE OR REPLACE TRIGGER
        if (nextWordIs(tokens, i + 1, 'TRIGGER')) sawTrigger = true;
        else if (nextWordIs(tokens, i + 1, 'OR') && nextWordIs(tokens, i + 2, 'REPLACE') && nextWordIs(tokens, i + 3, 'TRIGGER')) {
          sawTrigger = true;
        }
      }
    }
  }

  const wholeRange: OffsetRange = { start: statementRange.start, end: statementRange.end };

  if (engineId === 'mssql' && (sawOutput || sawMerge)) {
    out.push(makePseudo('inserted', wholeRange));
    out.push(makePseudo('deleted', wholeRange));
    out.push(makePseudo('$action', wholeRange));
  }
  if ((engineId === 'pgsql' || engineId === 'sqlite') && sawOnConflict) {
    out.push(makePseudo('excluded', wholeRange));
  }
  if (engineId === 'mysql' && sawInsertAsNew) {
    out.push(makePseudo('new', wholeRange));
  }
  if (sawTrigger) {
    if (engineId === 'oracle') {
      out.push(makePseudo(':NEW', wholeRange));
      out.push(makePseudo(':OLD', wholeRange));
    } else if (engineId === 'pgsql') {
      out.push(makePseudo('NEW', wholeRange));
      out.push(makePseudo('OLD', wholeRange));
      out.push(makePseudo('TG_OP', wholeRange));
      out.push(makePseudo('FOUND', wholeRange));
    } else if (engineId === 'mysql' || engineId === 'sqlite') {
      out.push(makePseudo('NEW', wholeRange));
      out.push(makePseudo('OLD', wholeRange));
    }
  }

  return out;
}

/**
 * Emit the trigger row pseudo-relations (`NEW` / `OLD`, Oracle's `:NEW` /
 * `:OLD`) as candidates in their own right, at an expression position inside
 * a trigger body.
 *
 * `collectPseudoTables` already BINDS them, which is what makes `NEW.▮`
 * resolve to the host table's columns — but nothing ever offered the
 * qualifier itself. Inside a trigger body there is no unqualified row scope,
 * so `NEW` / `OLD` is literally the first thing the user has to type to reach
 * a column, and it appeared in no list (mysql script.35 §4, sqlite
 * script.36 §4).
 *
 * The trigger's EVENT decides which row exists: an INSERT trigger has no
 * `OLD`, a DELETE trigger no `NEW`. The unavailable one is still emitted —
 * the user may be mid-edit on the event itself, and dropping a name that the
 * engine would reject is a worse failure than ranking it low — but it is
 * demoted out of the leading positions.
 *
 * Cross-engine: pgsql / mysql / sqlite / oracle all have the pseudo-rows and
 * all go through this emitter. MSSQL deliberately does NOT — T-SQL triggers
 * expose `inserted` / `deleted` as row SETS you select FROM, not per-row
 * qualifiers, so they belong to the relation emission, not here.
 */
export function emitTriggerPseudoRelationNames(
  out: CompletionCandidate[],
  source: string,
  statementRange: OffsetRange,
  aliasBindings: readonly AliasBinding[],
  engineId: DatabaseEngineId,
  /** Caret offset — the row qualifiers exist only once the header names the
   *  table they are rows OF (see `isInsideTriggerHeader`). */
  cursorOffset: number,
): void {
  if (engineId === 'mssql') return;
  const events = findTriggerEvents(source, statementRange, engineId);
  if (!events) return;
  // `CREATE TRIGGER t BEFORE UPDATE ON ▮` — still in the HEADER, where the
  // answer is the target TABLE. The row qualifiers are rows of a table the
  // statement has not named yet, so emitting them there is not just noisy:
  // they are `alias`-kind candidates at tier 1 and led the list, pushing the
  // recently-typed fixture table off the top (oracle coverage script.36 §3
  // pins `acact_trigger_target` first; the widget showed `:NEW`, `:OLD`).
  if (isInsideTriggerHeader(source, statementRange, engineId, cursorOffset)) return;
  for (const b of aliasBindings) {
    if (!b.relation.startsWith('__pseudo:')) continue;
    const bare = b.alias.replace(/^:/, '').toUpperCase();
    if (bare !== 'NEW' && bare !== 'OLD') continue;
    // `NEW` needs an INSERT or UPDATE; `OLD` needs an UPDATE or DELETE. With
    // no event parsed at all (a malformed / mid-typed header) both count as
    // available rather than both demoted.
    const available =
      events.length === 0 ||
      (bare === 'NEW'
        ? events.includes('INSERT') || events.includes('UPDATE')
        : events.includes('UPDATE') || events.includes('DELETE'));
    out.push({
      label: b.alias,
      insertText: b.alias,
      isSnippet: false,
      kind: 'alias',
      tier: available ? 1 : 4,
      detail: available
        ? `trigger row (${bare})`
        : `trigger row (${bare}) — not bound for ${events.join('/')} triggers`,
    });
  }
}

/**
 * The DML events of the `CREATE TRIGGER` header the cursor's statement
 * carries, or null when the statement is not a trigger definition.
 *
 * Bounded to the header: the scan stops at the `ON` that names the target,
 * so `INSERT` / `UPDATE` / `DELETE` statements in the trigger BODY are not
 * mistaken for events (`CREATE TRIGGER … INSTEAD OF INSERT ON v … BEGIN
 * INSERT INTO t … ; UPDATE u … ; END`).
 */
function findTriggerEvents(
  source: string,
  statementRange: OffsetRange,
  engineId: DatabaseEngineId,
): readonly ('INSERT' | 'UPDATE' | 'DELETE')[] | null {
  const tokens = tokenize(source, statementRange.start, statementRange.end, engineId);
  let i = 0;
  for (; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type !== 'kw' && t.type !== 'ident') continue;
    if (t.text.toUpperCase() !== 'TRIGGER') continue;
    break;
  }
  if (i >= tokens.length) return null;
  const events: ('INSERT' | 'UPDATE' | 'DELETE')[] = [];
  for (let j = i + 1; j < tokens.length; j += 1) {
    const t = tokens[j]!;
    if (t.type !== 'kw' && t.type !== 'ident') continue;
    const upper = t.text.toUpperCase();
    // `UPDATE OF col` keeps scanning (the `OF` list precedes `ON`); the target
    // `ON` ends the header.
    if (upper === 'ON') break;
    if (upper === 'INSERT' || upper === 'UPDATE' || upper === 'DELETE') {
      if (!events.includes(upper)) events.push(upper);
    }
  }
  return events;
}

/**
 * True while the caret is still inside a `CREATE TRIGGER` HEADER — up to and
 * including the `ON <target>` name. The row qualifiers (`NEW` / `OLD` /
 * `:NEW` / `:OLD`) are rows of that target, so they only come into scope once
 * it has been named; everything the header itself takes (timing keywords, the
 * event verbs, the target table) is answered by other emitters.
 *
 * A header with no `ON` yet counts as "inside": the target is still to come.
 * `ON <name>` counts as inside up to the end of the name so a partially typed
 * target (`ON acact_trig▮`) keeps the qualifiers out while the user types it.
 */
function isInsideTriggerHeader(
  source: string,
  statementRange: OffsetRange,
  engineId: DatabaseEngineId,
  cursorOffset: number,
): boolean {
  const tokens = tokenize(source, statementRange.start, statementRange.end, engineId);
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type !== 'kw' && t.type !== 'ident') continue;
    if (t.text.toUpperCase() !== 'ON') continue;
    // Schema-qualified targets (`ON hr.employees`) end at the last name part.
    let j = i + 1;
    if (tokens[j]?.type === 'ident' || tokens[j]?.type === 'kw') {
      while (tokens[j + 1]?.type === 'dot' && tokens[j + 2]) j += 2;
      return cursorOffset <= tokens[j]!.end;
    }
    return true;
  }
  return true;
}

function makePseudo(name: string, definedAt: OffsetRange): AliasBinding {
  return {
    alias: name,
    relation: `__pseudo:${name}`,
    kind: 'derived',
    definedAt,
  };
}

function nextWordIs(
  tokens: readonly { readonly type: string; readonly text: string }[],
  idx: number,
  expected: string,
): boolean {
  const t = tokens[idx];
  if (!t) return false;
  if (t.type !== 'ident' && t.type !== 'kw') return false;
  return t.text.toUpperCase() === expected;
}

function hasInsertContext(tokens: readonly { readonly type: string; readonly text: string }[]): boolean {
  // Walk a few tokens looking for INSERT — gives us a cheap heuristic for
  // "AS new" being the row-alias form vs an unrelated `AS` somewhere else.
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i];
    if (t.type === 'kw' && t.text.toUpperCase() === 'INSERT') return true;
  }
  return false;
}
